import React, { ReactChild } from 'react'
import { LocationObject } from '@elgatoylacaja/utils'

export type User = {
  accessToken: string
  experimentDone: boolean
  allowsNews?: boolean
  prevAllowsNews: boolean | null
}

export type AppContextData = {
  datetime: string
  auth: boolean
  visited: Array<string>
  user: User
  experiment: Experiment
  submitted: boolean
}

export type Empezar = {
  edad: string
  genero: string
  nivel_educativo: string
  nacionalidad: string
  pais_actual: string
  ciudad_actual: string
  personas_conviven: string
  ingreso: string
  trabaja_en_sistema_salud: string
  empleo_o_dedicacion: string
  mascotas: number[]
  otra_mascota: string
}

export type Percepcion = {
  contagiados: string
  quien_preocupa_porque: string
  peligroso: number
  vulnerable: number
  afectar_trabajo_ingresos: number
  afectar_plan_de_vida_proyectos: number
  peligroso_entorno: number
  vulnerable_entorno: number
}

export type OpinionCuarentena = {
  cumpliendo: string
  veces_salio: string
  cuan_de_acuerdo: number
  es_efectiva: number
}

export type ImportanciaMotivos = {
  proteger_salud_propia: number
  proteger_salud_personas_cercanas: number
  proteger_salud_sociedad: number
  cumplir_normativas: number
  evitar_multas_y_sanciones: number
}

export type CambiosDeHabitos = {
  cambios_de_habitos: number[]
  afirmaciones: number[]
  otro_cambio: string
}

export type RecomendacionesPreCuarentena = {
  lavarse_las_manos: number
  medidas_de_higiene_respiratoria: number
  distanciamiento_social: number
  evitar_tocarse: number
  al_tener_fiebre: number
  informado: number
  quedarse_en_casa: number
  no_tuvo_sintomas: boolean
  otra_medida: string
}

export type Dificultades = {
  trabajo: number
  actividades_de_ocio: number
  actividad_fisica: number
  actividades_del_hogar: number
  relaciones_con_adultos: number
  actividades_con_niñes: number
  no_convivo_con_adultos: boolean
  no_convivo_con_niñes: boolean
  formas_de_contacto: number[]
  habitos_que_dejo: string
  mas_extrañadas: string
  actividades_nuevas: string
  actividad_que_mejor_hace_sentir: string
  otra_forma_de_contacto: string
}

export type EventosUltimaSemana = {
  poco_placer: number
  sentirse_decaido: number
  dificultad_sueño: number
  cansancio: number
  cambios_apetito: number
  sentirse_mal_con_uno_mismo: number
  dificultad_para_concentrarse: number
  cambios_moviemiento: number
  nervios: number
  pensamientos_muerte_daño: number
  no_controlar_la_preocupación: number
  demasiada_preocupacion: number
  dificultad_para_relajarse: number
  estar_inquieto: number
  enojoso_irritable: number
  miedo_a_algo_terrible: number
  order: number[]
}

export type UltimasPreguntas = {
  animo_al_inicio: number
  estres_al_salir: number
  cuan_solo_se_siente: number
  importancia_estar_en_contacto: number
  felicidad_tiempo_solo: number
}

export type ParaTerminar = {
  en_trtamiento: string
  medicacion: string
  internado: string
  checkboxes: number[]
  familiar_necesita_ayuda: string
  algo_mas: string
}

export type Experiment = {
  login_fork: 1 | 2
  ubicacion_geo?: LocationObject
  empezar?: Empezar
  condiciones_previas?: number[]
  percepcion?: Percepcion
  opinion_cuarentena?: OpinionCuarentena
  salidas?: {
    motivos_salidas: number[]
    otro_motivo: string
  }
  importancia_motivos?: ImportanciaMotivos
  cambios_de_habitos?: CambiosDeHabitos
  recomendaciones_pre_cuarentena?: RecomendacionesPreCuarentena
  dificultades?: Dificultades
  eventos_ultima_semana?: EventosUltimaSemana
  ultimas_preguntas?: UltimasPreguntas
  para_terminar?: ParaTerminar
}

const initialAppContextData: () => AppContextData = () => {
  const login_fork = 2

  return {
    datetime: new Date().toLocaleString('es', { hour12: false }),
    auth: false,
    visited: [],
    experiment: {
      login_fork,
    },
    user: {
      accessToken: '',
      allowsNews: false,
      experimentDone: false,
      prevAllowsNews: null,
      service: 'email',
    },
    submitted: false,
  }
}

type Omit<T, K extends string | number | symbol> = {
  [P in Exclude<keyof T, K>]: T[P]
}

type SetGeolocation = {
  type: 'SET_GEOLOCATION'
  data: LocationObject
}

type GeolocationError = {
  type: 'GEOLOCATION_ERROR'
}

type Login = {
  type: 'LOGIN'
  user: Omit<User, 'allowsNews'>
}

type Visit = {
  type: 'REGISTER_VISIT'
  route: string
}

type AcceptTerms = {
  type: 'ACCEPT_TERMS'
  allowsNews: boolean
}

type SubmitEmpezar = {
  type: 'SUBMIT_EMPEZAR'
  data: Empezar
}

type SubmitCondicionesPrevias = {
  type: 'SUBMIT_CONDICIONES_PREVIAS'
  condiciones_previas: number[]
}

type SubmitPercepcion = {
  type: 'SUBMIT_PERCEPCION'
  data: Percepcion
}

type SubmitOpinionCuarentena = {
  type: 'SUBMIT_OPINION_CUARENTENA'
  data: OpinionCuarentena
}

type SubmitMotivosSalidas = {
  type: 'SUBMIT_MOTIVOS_SALIDAS'
  motivos_salidas: number[]
  otro_motivo: string
}

type SubmitImportanciaMotivos = {
  type: 'SUBMIT_IMPORTANCIA_MOTIVOS'
  data: ImportanciaMotivos
}

type SubmitCambiosDeHabitos = {
  type: 'SUBMIT_CAMBIOS_DE_HABITOS'
  data: CambiosDeHabitos
}

type SubmitRecomendacionesPreCuarentena = {
  type: 'SUBMIT_RECOMENDACIONES_PRE_CUARENTENA'
  data: RecomendacionesPreCuarentena
}

type SubmitDificultades = {
  type: 'SUBMIT_DIFICULTADES'
  data: Dificultades
}

type SubmitEventosUltimaSemana = {
  type: 'SUBMIT_EVENTOS_ULTIMA_SEMANA'
  data: EventosUltimaSemana
}

type SubmitUltimasPreguntas = {
  type: 'SUBMIT_ULTIMAS_PREGUNTAS'
  data: UltimasPreguntas
}

type SubmitParaTermiar = {
  type: 'SUBMIT_PARA_TERMINAR'
  data: ParaTerminar
}

type Submited = {
  type: 'SUBMITTED'
}

type ReducerAction =
  | AcceptTerms
  | Login
  | Visit
  | SubmitEmpezar
  | SubmitCondicionesPrevias
  | SubmitPercepcion
  | SubmitOpinionCuarentena
  | SubmitMotivosSalidas
  | SubmitImportanciaMotivos
  | SubmitCambiosDeHabitos
  | SubmitRecomendacionesPreCuarentena
  | SubmitDificultades
  | SubmitEventosUltimaSemana
  | SubmitUltimasPreguntas
  | SubmitParaTermiar
  | SetGeolocation
  | GeolocationError
  | Submited

const mutateExperimentState = (state: AppContextData) => <
  T extends keyof Experiment
>(
  key: T
) => (data: Experiment[T]) => ({
  ...state,
  experiment: {
    ...state.experiment,
    [key]: data,
  },
})

function reducer(state: AppContextData, action: ReducerAction): AppContextData {
  switch (action.type) {
    case 'SUBMITTED': {
      return {
        ...state,
        submitted: true,
      }
    }
    case 'LOGIN': {
      return {
        ...state,
        auth: true,
        user: {
          ...state.user,
          ...action.user,
        },
      }
    }
    case 'REGISTER_VISIT': {
      return {
        ...state,
        visited: [...state.visited, action.route],
      }
    }
    case 'ACCEPT_TERMS': {
      return {
        ...state,
        user: {
          ...state.user!,
          allowsNews: action.allowsNews,
        },
      }
    }
    case 'SUBMIT_EMPEZAR': {
      return mutateExperimentState(state)('empezar')(action.data)
    }
    case 'SUBMIT_CONDICIONES_PREVIAS': {
      return mutateExperimentState(state)('condiciones_previas')(
        action.condiciones_previas
      )
    }
    case 'SUBMIT_PERCEPCION': {
      return mutateExperimentState(state)('percepcion')(action.data)
    }
    case 'SUBMIT_OPINION_CUARENTENA': {
      return mutateExperimentState(state)('opinion_cuarentena')(action.data)
    }
    case 'SUBMIT_MOTIVOS_SALIDAS': {
      return mutateExperimentState(state)('salidas')({
        motivos_salidas: action.motivos_salidas,
        otro_motivo: action.otro_motivo,
      })
    }
    case 'SUBMIT_IMPORTANCIA_MOTIVOS': {
      return mutateExperimentState(state)('importancia_motivos')(action.data)
    }
    case 'SUBMIT_CAMBIOS_DE_HABITOS': {
      return mutateExperimentState(state)('cambios_de_habitos')(action.data)
    }
    case 'SUBMIT_RECOMENDACIONES_PRE_CUARENTENA': {
      return mutateExperimentState(state)('recomendaciones_pre_cuarentena')(
        action.data
      )
    }
    case 'SUBMIT_DIFICULTADES': {
      return mutateExperimentState(state)('dificultades')(action.data)
    }
    case 'SUBMIT_EVENTOS_ULTIMA_SEMANA': {
      return mutateExperimentState(state)('eventos_ultima_semana')(action.data)
    }
    case 'SUBMIT_ULTIMAS_PREGUNTAS': {
      return mutateExperimentState(state)('ultimas_preguntas')(action.data)
    }
    case 'SUBMIT_PARA_TERMINAR': {
      return mutateExperimentState(state)('para_terminar')(action.data)
    }
    case 'SET_GEOLOCATION': {
      return mutateExperimentState(state)('ubicacion_geo')(action.data)
    }
    case 'GEOLOCATION_ERROR': {
      return mutateExperimentState(state)('ubicacion_geo')(undefined)
    }
  }
}

export const AppContext = React.createContext({
  appState: initialAppContextData(),
  dispatch: (value: ReducerAction) => {},
})

export function AppContextProvider(props: { children: ReactChild }) {
  const [state, dispatch] = React.useReducer(reducer, initialAppContextData())

  return (
    <AppContext.Provider value={{ appState: state, dispatch }}>
      {props.children}
    </AppContext.Provider>
  )
}
